import { GridDateFilter } from 'lib/grid/GridDateFilter';
import { Box } from '@mui/material';
import React from 'react';
import { Row } from 'ui/Flex';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import {
    PepperTransactionsFilter,
    PepperTransactionsFilterChange,
    transactionsFilterOptions
} from '../hooks/usePepperTransactionsFilter';
import { TransactionsLastFourFilter } from './TransactionsLastFourFilter';
import { GridLocationFilter } from 'lib/grid/GridLocationFilter';
import { GridFilterInput } from 'lib/grid/GridFilterInput';
import { TransactionExpiryInput } from './TransactionExpiryFilter';
import { isEmptyString, isString } from 'lib/typeguards';
import { TransactionsSelectFilter } from './TransactionsSelectFilter';
import { defaultRangeShortcutsItems } from 'lib/date-picker/helpers';

interface TransactionsFiltersProps {
    filter: PepperTransactionsFilter;
    onChange: PepperTransactionsFilterChange;
}

export const TransactionsFilters: React.FC<TransactionsFiltersProps> = ({ filter, onChange }) => {
    const handleDateChange = React.useCallback(
        (value?: DateRangePickerValue) => {
            onChange('date', value);
        },
        [onChange]
    );
    const handleTextChange = React.useCallback(
        (value: string, name: keyof PepperTransactionsFilter) => {
            onChange(name, value);
        },
        [onChange]
    );
    const handleLast4Change = React.useCallback(
        (value?: string) => {
            onChange('last4', value);
        },
        [onChange]
    );
    const handleExpiryChange = React.useCallback(
        (value?: string) => {
            onChange('expiryDate', value);
        },
        [onChange]
    );
    const handleLocationChange = React.useCallback(
        (value?: string) => {
            onChange('locationId', value);
        },
        [onChange]
    );
    const isOrderIdPresent = React.useMemo(
        () => isString(filter.orderId) && !isEmptyString(filter.orderId) && Number(filter.orderId) >= 0,
        [filter.orderId]
    );
    return (
        <Row flex={1} wrap="wrap" valign="center">
            <Box marginTop={1.5} marginRight={1.5}>
                <GridDateFilter
                    value={filter.date}
                    onChange={handleDateChange}
                    clearable={false}
                    disabled={isOrderIdPresent}
                    maxRangeMonths={3}
                    size="small"
                    paper
                    shortcuts={defaultRangeShortcutsItems}
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <GridFilterInput
                    value={filter.paymentIntentId}
                    onChange={handleTextChange}
                    label="Payment Intent ID"
                    name="paymentIntentId"
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsSelectFilter
                    value={filter.state}
                    onChange={onChange}
                    disabled={isOrderIdPresent}
                    options={transactionsFilterOptions.state}
                    unselectedLabel="All transaction states"
                    name="state"
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsSelectFilter
                    value={filter.walletType}
                    onChange={onChange}
                    disabled={isOrderIdPresent}
                    options={transactionsFilterOptions.wallet}
                    unselectedLabel="All wallet types"
                    name="walletType"
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsSelectFilter
                    value={filter.risk}
                    onChange={onChange}
                    disabled={isOrderIdPresent}
                    options={transactionsFilterOptions.risk}
                    unselectedLabel="All risk levels"
                    name="risk"
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <GridFilterInput
                    value={filter.orderId}
                    onChange={handleTextChange}
                    label="Order Number"
                    name="orderId"
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsSelectFilter
                    value={filter.type}
                    onChange={onChange}
                    disabled={isOrderIdPresent}
                    options={transactionsFilterOptions.type}
                    unselectedLabel="All transaction types"
                    name="type"
                    size="small"
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsLastFourFilter
                    value={filter.last4}
                    onChange={handleLast4Change}
                    disabled={isOrderIdPresent}
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <GridLocationFilter
                    value={filter.locationId}
                    onChange={handleLocationChange}
                    disabled={isOrderIdPresent}
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <GridFilterInput
                    value={filter.customerId}
                    onChange={handleTextChange}
                    label="Customer ID"
                    name="customerId"
                    disabled={isOrderIdPresent}
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionExpiryInput
                    value={filter.expiryDate}
                    onChange={handleExpiryChange}
                    disabled={isOrderIdPresent}
                    size="small"
                    paper
                />
            </Box>
            <Box marginTop={1.5} marginRight={1.5}>
                <TransactionsSelectFilter
                    value={filter.method}
                    onChange={onChange}
                    disabled={isOrderIdPresent}
                    options={transactionsFilterOptions.method}
                    unselectedLabel="Any transaction method"
                    name="method"
                    size="small"
                    paper
                />
            </Box>
        </Row>
    );
};
